// Create / edit clinic modal (M2). One panel serves both modes: a null `clinic` prop is
// create (validates createClinicSchema → POST /clinics), a Clinic is edit (validates
// updateClinicSchema → PATCH /clinics/:id). Mirrors NewCaseModal's overlay + safeParse +
// queueable-submit shape: an offline submit parks in the queue and is treated as a success.
import { useEffect, useState } from 'react';
import {
  type Clinic,
  type ClientType,
  type ClinicPortalStatus,
  type ClinicPortalAccessResult,
  createClinicSchema,
  updateClinicSchema,
  clinicPortalAccessSchema,
  clinicPortalResetSchema,
  CLIENT_TYPES,
  CLIENT_TYPE_LABELS,
} from '@alignova/shared';
import { api, isDeferred, ApiError } from '../../lib/api.ts';
import { t } from '../../i18n/index.ts';
import { PasswordInput } from '../../ui/PasswordInput.tsx';
import { WizardFooter, WizardStepper } from '../../ui/Wizard.tsx';
import { Button, Card, Pill } from '../../ui/primitives.tsx';

const inputClass = 'w-full rounded border border-line bg-surface px-3 py-2 text-sm';

/**
 * The login, ready to hand over.
 *
 * Shown once, immediately after the password is set, because that is the only moment the lab can
 * legitimately see it: what the database holds is a bcrypt hash, and it stays that way so a copy of
 * the database is not a list of every clinic's password — the one they very likely also use for
 * something that matters more.
 *
 * If it is lost, the answer is to set a new one, which takes two clicks. That is a better trade
 * than a system where anybody with console access can read every customer's password.
 */
function HandOver({
  issued,
  clinicName,
  copied,
  onCopied,
}: {
  issued: { username: string; password: string };
  clinicName: string;
  copied: string | null;
  onCopied: (what: string | null) => void;
}) {
  const message =
    `Alignova portal login for ${clinicName || 'your clinic'}\n` +
    `Sign in at https://alignova.cloud\n` +
    `Username: ${issued.username}\n` +
    `Password: ${issued.password}`;

  const copy = (text: string, what: string) => {
    void navigator.clipboard?.writeText(text).then(() => {
      onCopied(what);
      setTimeout(() => onCopied(null), 1800);
    });
  };

  return (
    <div className="mb-4 rounded-lg border border-status-progress-fg/30 bg-status-progress-bg/50 p-4">
      <p className="text-sm font-semibold text-status-progress-fg">Login ready to send</p>
      <p className="mt-0.5 text-xs text-status-progress-fg/90">
        Copy it now - it cannot be shown again. Passwords are stored one-way, so nobody at the lab
        can look one up later; if it is lost, set a new one here.
      </p>

      <dl className="mt-3 space-y-1.5">
        {([
          ['Username', issued.username],
          ['Password', issued.password],
        ] as Array<[string, string]>).map(([k, v]) => (
          <div key={k} className="flex items-center gap-2">
            <dt className="w-20 shrink-0 text-xs font-medium uppercase tracking-wide text-status-progress-fg/80">{k}</dt>
            <dd className="min-w-0 flex-1 truncate rounded bg-surface px-2.5 py-1.5 font-mono text-sm text-ink">{v}</dd>
            <button
              type="button"
              onClick={() => copy(v, k)}
              className="tap rounded border border-line bg-surface px-2 py-1.5 text-xs font-medium text-ink hover:bg-surface-2"
            >
              {copied === k ? 'Copied' : 'Copy'}
            </button>
          </div>
        ))}
      </dl>

      <button
        type="button"
        onClick={() => copy(message, 'message')}
        className="tap mt-3 rounded-md bg-status-progress-fg px-3.5 py-2 text-sm font-semibold text-white"
      >
        {copied === 'message' ? 'Message copied' : 'Copy the whole message'}
      </button>
    </div>
  );
}

export function ClinicModal({
  open,
  clinic,
  onClose,
  onSaved,
  /**
   * 'portal' opens straight onto the credentials panel and hides the clinic form. The Clinics list
   * uses it so granting or resetting a login is one click, rather than opening the full editor and
   * scrolling past eleven fields to reach the section that was wanted.
   */
  focus = 'all',
}: {
  open: boolean;
  clinic: Clinic | null;
  onClose: () => void;
  onSaved: () => void;
  focus?: 'all' | 'portal';
}) {
  const isEdit = clinic !== null;
  // A portal-only view of a clinic that doesn't exist yet is meaningless, so fall back to the form.
  const portalOnly = focus === 'portal' && isEdit;

  const [clinicCode, setClinicCode] = useState('');
  const [name, setName] = useState('');
  const [clientType, setClientType] = useState<ClientType>('clinic');
  const [dentistName, setDentistName] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [mobile, setMobile] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [email, setEmail] = useState('');
  const [city, setCity] = useState('');
  const [address, setAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const STEPS = ['Clinic', 'Contact', 'Location'] as const;
  const [step, setStep] = useState(0);
  // Only a new clinic is walked through. Editing shows everything, as it always did.
  const stepwise = !isEdit && !portalOnly;
  const lastStep = STEPS.length - 1;
  // The two fields the record cannot exist without. Everything after this is worth having, not
  // required, so nothing later blocks somebody from finishing.
  // The code assigns itself, so only the name gates the first step now.
  const canLeaveFirst = name.trim().length > 0;
  const canContinue = step === 0 ? canLeaveFirst : true;
  /** Which step a field belongs to. Editing shows them all at once. */
  const on = (n: number) => !stepwise || step === n;

  // --- Portal access (clinic-portal login) ---
  const [portal, setPortal] = useState<ClinicPortalStatus | null>(null);
  const [pUsername, setPUsername] = useState('');
  const [pDisplayName, setPDisplayName] = useState('');
  const [pEmail, setPEmail] = useState('');
  const [pPassword, setPPassword] = useState('');
  const [portalMsg, setPortalMsg] = useState<string | null>(null);
  const [portalErr, setPortalErr] = useState<string | null>(null);
  const [portalBusy, setPortalBusy] = useState(false);
  /** The password just set, held only for as long as this panel is open so it can be handed over. */
  const [issued, setIssued] = useState<{ username: string; password: string } | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  // Prefill from the target clinic each time the modal opens (edit), or clear for create.
  useEffect(() => {
    if (!open) return;
    setStep(0);
    // A password is never carried from one clinic's panel into the next.
    setIssued(null);
    setCopied(null);
    setClinicCode(clinic?.clinicCode ?? '');
    setName(clinic?.name ?? '');
    setClientType(clinic?.clientType ?? 'clinic');
    setDentistName(clinic?.dentistName ?? '');
    setContactPerson(clinic?.contactPerson ?? '');
    setMobile(clinic?.mobile ?? '');
    setWhatsapp(clinic?.whatsapp ?? '');
    setEmail(clinic?.email ?? '');
    setCity(clinic?.city ?? '');
    setAddress(clinic?.address ?? '');
    setNotes(clinic?.notes ?? '');
    setError(null);
    setSubmitting(false);
    // Portal-access section: seed from the clinic's current login state + sensible defaults.
    setPortal(clinic?.portal ?? null);
    setPUsername(clinic?.clinicCode ? clinic.clinicCode.toLowerCase() : '');
    setPDisplayName(clinic?.name ?? '');
    setPEmail(clinic?.email ?? '');
    setPPassword('');
    setPortalMsg(null);
    setPortalErr(null);
    setPortalBusy(false);
  }, [open, clinic]);

  if (!open) return null;

  function portalErrorMessage(e: unknown): string {
    return e instanceof ApiError && e.message ? e.message : t('clinics.portal.error');
  }

  async function createPortalLogin() {
    if (!clinic) return;
    setPortalErr(null);
    setPortalMsg(null);
    const parsed = clinicPortalAccessSchema.safeParse({
      username: pUsername,
      password: pPassword,
      displayName: pDisplayName,
      email: pEmail.trim() || undefined,
    });
    if (!parsed.success) {
      setPortalErr(parsed.error.issues[0]?.message ?? t('clinics.portal.error'));
      return;
    }
    setPortalBusy(true);
    try {
      const res = await api.post<ClinicPortalAccessResult>(`/clinics/${clinic.id}/portal-access`, parsed.data);
      setPortal({ userId: res.userId, username: res.username, active: res.active });
      setIssued({ username: res.username, password: parsed.data.password });
      setPPassword('');
      setPortalMsg(t('clinics.portal.created'));
      onSaved();
    } catch (e) {
      setPortalErr(portalErrorMessage(e));
    } finally {
      setPortalBusy(false);
    }
  }

  async function resetPortalPassword() {
    if (!clinic) return;
    setPortalErr(null);
    setPortalMsg(null);
    const parsed = clinicPortalResetSchema.safeParse({ password: pPassword });
    if (!parsed.success) {
      setPortalErr(parsed.error.issues[0]?.message ?? t('clinics.portal.error'));
      return;
    }
    setPortalBusy(true);
    try {
      await api.post(`/clinics/${clinic.id}/portal-access/reset`, parsed.data);
      setIssued({ username: portal?.username ?? pUsername, password: parsed.data.password });
      setPPassword('');
      setPortalMsg(t('clinics.portal.resetDone'));
      onSaved();
    } catch (e) {
      setPortalErr(portalErrorMessage(e));
    } finally {
      setPortalBusy(false);
    }
  }

  async function togglePortalActive() {
    if (!clinic || !portal) return;
    setPortalErr(null);
    setPortalMsg(null);
    setPortalBusy(true);
    try {
      const res = await api.post<ClinicPortalAccessResult>(`/clinics/${clinic.id}/portal-access/active`, {
        active: !portal.active,
      });
      setPortal({ userId: res.userId, username: res.username, active: res.active });
      setPortalMsg(t('clinics.portal.statusChanged'));
      onSaved();
    } catch (e) {
      setPortalErr(portalErrorMessage(e));
    } finally {
      setPortalBusy(false);
    }
  }

  async function submit(e?: React.FormEvent) {
    e?.preventDefault();
    setError(null);

    // The mobile number identifies the clinic — it is how staff find the practice and how its cases
    // reach its portal — so a new clinic cannot be filed without one. Step 1 holds the field, so
    // send the user there rather than showing an error on a step that does not contain it.
    if (!clinic && !mobile.trim()) {
      setError(t('clinics.form.mobileRequired'));
      if (stepwise) setStep(1);
      return;
    }

    // Normalise empty optionals to undefined so nullish schema fields stay clean.
    const optionals = {
      dentistName: dentistName.trim() || undefined,
      contactPerson: contactPerson.trim() || undefined,
      mobile: mobile.trim() || undefined,
      whatsapp: whatsapp.trim() || undefined,
      email: email.trim() || undefined,
      city: city.trim() || undefined,
      address: address.trim() || undefined,
      notes: notes.trim() || undefined,
    };

    setSubmitting(true);
    try {
      if (clinic) {
        const parsed = updateClinicSchema.safeParse({
          name,
          clientType,
          ...optionals,
        });
        if (!parsed.success) {
          setError(parsed.error.issues[0]?.message ?? t('clinics.form.invalid'));
          setSubmitting(false);
          return;
        }
        const res = await api.patch<Clinic>(`/clinics/${clinic.id}`, parsed.data, { queue: true });
        if (isDeferred(res)) {
          onSaved();
          onClose();
          return;
        }
      } else {
        const parsed = createClinicSchema.safeParse({
          // Omitted entirely when blank, which is what tells the server to allocate one.
          clinicCode: clinicCode.trim() || undefined,
          name,
          clientType,
          ...optionals,
        });
        if (!parsed.success) {
          setError(parsed.error.issues[0]?.message ?? t('clinics.form.invalid'));
          setSubmitting(false);
          return;
        }
        const res = await api.post<Clinic>('/clinics', parsed.data, { queue: true });
        if (isDeferred(res)) {
          onSaved();
          onClose();
          return;
        }
      }
      onSaved();
      onClose();
    } catch {
      setError(t('clinics.form.saveError'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center bg-black/50 p-4">
      <Card className="max-h-screen w-full max-w-2xl overflow-y-auto p-6">
        <h2 className="mb-4 text-lg font-semibold text-ink">
          {portalOnly
            ? `${t('clinics.portal.only')} - ${clinic!.name}`
            : isEdit
              ? t('clinics.form.editTitle')
              : t('clinics.form.createTitle')}
        </h2>
        <form className={`space-y-4 ${portalOnly ? 'hidden' : ''}`} onSubmit={submit}>
          {stepwise && <WizardStepper steps={STEPS} step={step} onJump={setStep} />}

          <div className={`grid grid-cols-1 gap-x-4 gap-y-3 ${on(0) ? '' : 'hidden'}`}>
            <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
              <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('clinics.form.clinicCode')}</label>
              <div>
                <input
                  className={inputClass}
                  value={clinicCode}
                  onChange={(e) => setClinicCode(e.target.value)}
                  disabled={isEdit}
                  placeholder={isEdit ? '' : t('clinics.form.codeAuto')}
                />
                {!isEdit && !clinicCode.trim() && (
                  <p className="mt-1 text-xs text-muted">{t('clinics.form.codeAutoHint')}</p>
                )}
              </div>
            </div>
            <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
              <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('clinics.form.clientType')}</label>
              <select
                className={inputClass}
                value={clientType}
                onChange={(e) => setClientType(e.target.value as ClientType)}
              >
                {CLIENT_TYPES.map((ct) => (
                  <option key={ct} value={ct}>
                    {CLIENT_TYPE_LABELS[ct]}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className={`md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4 ${on(0) ? '' : 'hidden'}`}>
            <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('clinics.form.name')}</label>
            <input
              className={inputClass}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className={`grid grid-cols-1 gap-x-4 gap-y-3 ${on(1) ? '' : 'hidden'}`}>
            <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
              <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('clinics.form.dentistName')}</label>
              <input
                className={inputClass}
                value={dentistName}
                onChange={(e) => setDentistName(e.target.value)}
              />
            </div>
            <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
              <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">
                {t('clinics.form.contactPerson')}
              </label>
              <input
                className={inputClass}
                value={contactPerson}
                onChange={(e) => setContactPerson(e.target.value)}
              />
            </div>
          </div>
          <div className={`grid grid-cols-1 gap-x-4 gap-y-3 ${on(1) ? '' : 'hidden'}`}>
            <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
              <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('clinics.form.mobile')} *</label>
              <input
                className={inputClass}
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
              />
            </div>
            <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
              <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('clinics.form.whatsapp')}</label>
              <input
                className={inputClass}
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
              />
            </div>
          </div>
          <div className={`md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4 ${on(1) ? '' : 'hidden'}`}>
            <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('clinics.form.email')}</label>
            <input
              type="email"
              className={inputClass}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className={`md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4 ${on(2) ? '' : 'hidden'}`}>
            <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('clinics.form.city')}</label>
            <input
              className={inputClass}
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
          </div>
          <div className={`md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4 ${on(2) ? '' : 'hidden'}`}>
            <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('clinics.form.address')}</label>
            <input
              className={inputClass}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
          </div>
          <div className={`md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4 ${on(2) ? '' : 'hidden'}`}>
            <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('clinics.form.notes')}</label>
            <textarea
              className={inputClass}
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          {error && <p className="text-xs text-status-urgent-fg">{error}</p>}

          {stepwise ? (
            <WizardFooter
              step={step}
              last={lastStep}
              canContinue={canContinue}
              busy={submitting}
              submitLabel={submitting ? t('clinics.form.saving') : t('clinics.form.submit')}
              onBack={() => setStep((n) => Math.max(0, n - 1))}
              onNext={() => setStep((n) => Math.min(lastStep, n + 1))}
              onSubmit={() => void submit()}
              onCancel={onClose}
            />
          ) : (
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={onClose}>
                {t('clinics.form.cancel')}
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? t('clinics.form.saving') : t('clinics.form.submit')}
              </Button>
            </div>
          )}
        </form>

        {/* Clinic portal access - create the login, reset its password, or enable/disable it. */}
        <div className={portalOnly ? '' : 'mt-6 border-t border-line pt-5'}>
          {issued && <HandOver issued={issued} clinicName={name} copied={copied} onCopied={setCopied} />}
          <div className="mb-1 flex items-center gap-2">
            <h3 className="text-sm font-semibold text-ink">{t('clinics.portal.section')}</h3>
            {isEdit &&
              (portal ? (
                portal.active ? (
                  <Pill tone="good">{t('clinics.portal.active')}</Pill>
                ) : (
                  <Pill tone="bad">{t('clinics.portal.disabled')}</Pill>
                )
              ) : (
                <Pill tone="neutral">{t('clinics.portal.none')}</Pill>
              ))}
          </div>
          <p className="mb-3 text-xs text-muted">{t('clinics.portal.hint')}</p>

          {!isEdit ? (
            <p className="text-xs text-muted">{t('clinics.portal.saveFirst')}</p>
          ) : portal ? (
            <div className="space-y-3">
              <div className="text-sm">
                <span className="text-muted">{t('clinics.portal.username')}: </span>
                <span className="font-mono">{portal.username}</span>
              </div>
              <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-center md:gap-4">
                <label className="mb-1 block text-sm text-muted md:mb-0">
                  {t('clinics.portal.newPassword')}
                </label>
                <PasswordInput
                  className={inputClass}
                  value={pPassword}
                  onChange={(e) => setPPassword(e.target.value)}
                  autoComplete="new-password"
                  placeholder="••••••••"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" onClick={resetPortalPassword} disabled={portalBusy}>
                  {t('clinics.portal.reset')}
                </Button>
                <Button type="button" variant="ghost" onClick={togglePortalActive} disabled={portalBusy}>
                  {portal.active ? t('clinics.portal.disable') : t('clinics.portal.enable')}
                </Button>
              </div>

            </div>
          ) : (
            <div className="space-y-3">
              <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-center md:gap-4">
                <label className="mb-1 block text-sm text-muted md:mb-0">{t('clinics.portal.username')}</label>
                <input
                  className={inputClass}
                  value={pUsername}
                  onChange={(e) => setPUsername(e.target.value)}
                  autoComplete="off"
                />
              </div>
              <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-center md:gap-4">
                <label className="mb-1 block text-sm text-muted md:mb-0">{t('clinics.portal.displayName')}</label>
                <input
                  className={inputClass}
                  value={pDisplayName}
                  onChange={(e) => setPDisplayName(e.target.value)}
                />
              </div>
              <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-center md:gap-4">
                <label className="mb-1 block text-sm text-muted md:mb-0">{t('clinics.portal.email')}</label>
                <input
                  type="email"
                  className={inputClass}
                  value={pEmail}
                  onChange={(e) => setPEmail(e.target.value)}
                  autoComplete="off"
                />
              </div>
              <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-center md:gap-4">
                <label className="mb-1 block text-sm text-muted md:mb-0">{t('clinics.portal.password')}</label>
                <PasswordInput
                  className={inputClass}
                  value={pPassword}
                  onChange={(e) => setPPassword(e.target.value)}
                  autoComplete="new-password"
                  placeholder="min 8 characters"
                />
              </div>
              <div>
                <Button type="button" onClick={createPortalLogin} disabled={portalBusy}>
                  {t('clinics.portal.create')}
                </Button>
              </div>
            </div>
          )}

          {portalMsg && <p className="mt-3 text-xs text-status-progress-fg">{portalMsg}</p>}
          {portalErr && <p className="mt-3 text-xs text-status-urgent-fg">{portalErr}</p>}

          {/* The only Cancel lives inside the clinic form, which portal-only mode hides - without
              this there would be no way out of the dialog. */}
          {portalOnly && (
            <div className="mt-5 flex justify-end gap-2 border-t border-line pt-4">
              <Button type="button" variant="ghost" onClick={onClose}>
                {t('clinics.form.cancel')}
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
